import React, { useState, useEffect } from 'react';
import Pagination from '@mui/material/Pagination';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import EntityColumns from './entityTable/EntityColumns';
import ProductsColumns from './commodityTable/CommodityColumns';
import CyberColumns from './cyberTable/CyberColumns';
import EntityRow from './entityTable/EntityRow';
import ProductRow from './commodityTable/CommodityRow';
import CyberRow from './cyberTable/CyberRow';
import { exportToCSV } from '../../../components/Common/CSVManage';
import { Typography } from '@mui/material';
import {
  NextButton,
  StyledInput,
} from '../../../components/Common/MUIStyledComponents';
import Modal from 'react-modal';
import { splitRiskSignals } from 'utils/entityRiskSignals';

interface ISingleScreeningProps {
  results: any;
  page: number;
  setPage: any;
  handleSubmit: any;
  type: string;
  setShowFullRecord: any;
  cyberHeaders: string[];
  getCSVResults?: any;
  setTicketNameOpen: any;
  inputs: any;
  handleOpenModal?: any;
}
interface ScreeningResult {
  'Name of Entity': string;
  'Entity Country': string;
  'List Name': string;
  Agency?: string;
  'Additional Remarks'?: string;
  'Date Updated on BITE Platform'?: string;
}

const SingleScreeningResults: React.FC<ISingleScreeningProps> = ({
  setShowFullRecord,
  type,
  handleSubmit,
  setPage,
  page,
  results,
  cyberHeaders,
  getCSVResults,
  setTicketNameOpen,
  inputs,
}) => {
  const totalResults = results?.data[0]?.result?.body?.hits?.total?.value;
  const resultsPerPage = 50;
  const [ pageCount, setPageCount ] = useState<number>(0);
  const [ fileName, setFileName ] = useState<string>('');
  const [ fileNameOpen, setFileNameOpen ] = useState(false);
  const [selectedProductHit, setSelectedProductHit] = useState<any>(null);
  const [isProductDrawerOpen, setIsProductDrawerOpen] = useState(false);
  const [ hitsData, setHitsData ] = useState<any>(
    results?.data[0]?.result?.body?.hits?.hits,
  );
  const [ sortConfig, setSortConfig ] = useState<{
    key: keyof ScreeningResult;
    direction: 'asc' | 'desc';
  }>({
    key: 'Name of Entity',
    direction: 'asc',
  });

  const handlePageChange = (e: any, new_page: number) => {
    const handledPage = new_page - 1;
    setPage(handledPage);
    handleSubmit(handledPage);
  };

  const entHeaders = [
    'Name of Entity',
    'Entity Country',
    'List Name',
    'Agency',
    'Additional Remarks',
    'Date Updated',
  ];
  const comHeaders = [
    'Product Name',
    'HS Code',
    'Source Country',
    'Agency',
    'List Name',
    'Date Updated',
  ];
  const tradeAlertsHeaders = [
    'Intervention Type',
    'HS Code',
    'Implementing Jurisdictions',
    'Risk Country',
    'GTA Evaluation',
  ];
  const cveHeaders = [
    'CVE ID',
    'Vendor Project',
    'Product',
    'Vulnerability Name',
    'Required Action',
  ];
  const markHeaders = [
    'Market Name',
    'Description',
    'Website',
    'Market Location',
  ];

  const handleCSV = async() => {
    const res = await getCSVResults(0);
    if (res[0].result?.body?.hits?.hits.length >= 1) {
      exportToCSV(
        fileName,
        [ ...res[0].result?.body?.hits?.hits ],
        type === 'Entities' ?
          entHeaders :
          type === 'Products' ?
            comHeaders :
            cyberHeaders?.includes('CVE ID') ?
              cveHeaders :
              cyberHeaders?.includes('Intervention Type') ?
                tradeAlertsHeaders :
                markHeaders,
        type === 'Entities' ?
          'entity' :
          type === 'Products' ?
            'commodity' :
            cyberHeaders?.includes('CVE ID') ?
              'CVE' :
              cyberHeaders?.includes('Intervention Type') ?
                'TradeAlerts' :
                'Markets',
      );
    }
  };

  const handleFileNameChange = (e: any) => {
    setFileName(e.target.value);
  };

  const handleFileNameClose = () => {
    setFileNameOpen(false);
    setFileName('');
  };

  const hasDisplayValue = (value: any) => {
    if (value === null || value === undefined) return false;
    if (Array.isArray(value)) return value.filter(Boolean).length > 0;
    if (typeof value === 'object') return Object.keys(value).length > 0;
    return String(value).trim() !== '';
  };

  const formatFieldValue = (value: any): string => {
    if (Array.isArray(value)) return value.filter(Boolean).join(', ');
    if (value && typeof value === 'object') return JSON.stringify(value);
    return String(value ?? '');
  };

  // Detects a URL-looking token. Accepts bare domains starting with www. and
  // any http(s) link. Keep the regex narrow — we render arbitrary record
  // fields and don't want stray text turned into links.
  const URL_TOKEN_REGEX = /\b((?:https?:\/\/|www\.)[^\s<>"'`]+)/gi;

  const renderFieldWithLinks = (value: string): React.ReactNode => {
    if (!value) return value;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let key = 0;
    const text = value;
    for (const match of text.matchAll(URL_TOKEN_REGEX)) {
      const url = match[0];
      const start = match.index ?? 0;
      if (start > lastIndex) {
        parts.push(text.slice(lastIndex, start));
      }
      // Strip trailing punctuation that's almost certainly sentence
      // terminators rather than part of the URL.
      let cleaned = url;
      let trailing = '';
      while (
        cleaned.length > 0 &&
        '.,);:!?'.includes(cleaned[cleaned.length - 1])
      ) {
        trailing = cleaned[cleaned.length - 1] + trailing;
        cleaned = cleaned.slice(0, -1);
      }
      const href = cleaned.startsWith('http') ? cleaned : `https://${cleaned}`;
      parts.push(
        <a
          key={`url-${key++}`}
          href={href}
          target='_blank'
          rel='noopener noreferrer'
          className='link'
        >
          {cleaned}
        </a>,
      );
      if (trailing) parts.push(trailing);
      lastIndex = start + url.length;
    }
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }
    return parts.length > 0 ? parts : value;
  };

  const getProductSource = (hit: any) => hit?._source || {};
  const productRows = Array.isArray(hitsData) ? hitsData : [];
  const selectedProductSource = getProductSource(selectedProductHit);
  const {
    signals: selectedProductRiskSignals,
    rest: selectedProductDetails,
  } = splitRiskSignals(selectedProductSource);
  const selectedProductIndex =
    selectedProductHit
      ? productRows.findIndex((hit: any) => hit === selectedProductHit)
      : -1;
  const canNavigateProductPrev = selectedProductIndex > 0;
  const canNavigateProductNext =
    selectedProductIndex >= 0 && selectedProductIndex < productRows.length - 1;

  const handleProductRowSelect = (hit: any) => {
    setSelectedProductHit(hit);
    setIsProductDrawerOpen(true);
  };

  const handleProductDrawerPrev = () => {
    if (canNavigateProductPrev) {
      setSelectedProductHit(productRows[selectedProductIndex - 1]);
    }
  };

  const handleProductDrawerNext = () => {
    if (canNavigateProductNext) {
      setSelectedProductHit(productRows[selectedProductIndex + 1]);
    }
  };

  useEffect(() => {
    const totalPages = Math.ceil(totalResults / resultsPerPage);
    if (totalPages >= 200) {
      setPageCount(199);
    } else if (totalPages <= 199) {
      setPageCount(totalPages);
    }
  }, [ results ]);

  useEffect(() => {
    setHitsData(results?.data[0]?.result?.body?.hits?.hits || []);
    setSelectedProductHit(null);
    setIsProductDrawerOpen(false);
  }, [results]);

  useEffect(() => {
    if (results.data[0].result.body.hits.hits.length === 0) {
      setPage(0);
    }
  }, [ results.data[0] ]);

  const fieldMap: Record<string, string> = {
    'nameOfEntity': 'Name of Entity',
    'nameOfCountry': 'Entity Country',
    'listName': 'List Name',
    'agency': 'Agency',
    'addRemarks': 'Additional Remarks',
    'dateUpdated': 'Date Updated on BITE Platform',
    'productName': 'Product Name',
    'hsCode': 'HS Code',
    'sourceCountry': 'Source Country',
    'CVE ID': 'CVE ID',
    'Vendor Project': 'Vendor Project',
    'Product': 'Product',
    'Vulnerability Name': 'Vulnerability Name',
    'Required Action': 'Required Action',
    'Market Name': 'Market Name',
    'Description': 'Description',
    'Website': 'Website',
    'Market Location': 'Market Location',
  };

  const compareValues = (
    aVal: string,
    bVal: string,
    direction: 'asc' | 'desc',
  ) => {
    const aIsEmpty = !aVal?.trim();
    const bIsEmpty = !bVal?.trim();

    if (aIsEmpty && !bIsEmpty) {
      return direction === 'asc' ? -1 : 1;
    }
    if (!aIsEmpty && bIsEmpty) {
      return direction === 'asc' ? 1 : -1;
    }
    if (aIsEmpty && bIsEmpty) {
      return 0;
    }

    return direction === 'asc' ?
      aVal.localeCompare(bVal) :
      bVal.localeCompare(aVal);
  };

  const sortData = (key: string) => {
    const typedKey = key as keyof ScreeningResult;
    const sortedData = [ ...hitsData ];

    const field = fieldMap[key];
    if (!field) {
      return;
    }

    const direction = sortConfig.direction;
    sortedData.sort((a, b) =>
      compareValues(
        a._source?.[field] || '',
        b._source?.[field] || '',
        direction,
      ),
    );

    // Toggle sort direction
    const newDirection = direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key: typedKey, direction: newDirection });
    setHitsData(sortedData);
  };

  return (
    <div className='screening-results-container'>
      <div className='screening-results-header'>
        <div className='screening-results-header-group'>
          <div className='screening-results-header-frame'>
            <div className='screening-results-matches'>
              <div className='screening-results-matches-inner'>
                <div className='screening-matches-text'>
                  {totalResults ? totalResults : 0} results found
                </div>
              </div>
            </div>
            {((inputs.primary && inputs.primary.length > 0) ||
              (inputs.hsCode && inputs.hsCode.length > 0) ||
              (inputs.desc && inputs.desc.length > 0)) && (
              <div className='screening-input-label-text-table'>
              Save to BITE&apos;s Saved Work tool to review screening results later and set up a periodic screening of your search, or download a csv of your results now
              </div>
            )
            }
            {((inputs.primary && inputs.primary.length > 0) || (inputs.hsCode && inputs.hsCode.length > 0) || (inputs.desc && inputs.desc.length > 0)) &&
            <div className='screening-results-download-section'>
              <div className='screening-results-download-btn'>
                <div className='screening-results-section-text-padding'>
                  <div className='screening-results-section-text' onClick={() => setTicketNameOpen(true)}>
                    Save
                  </div>
                </div>
              </div>
            </div>
            }

            <div className='screening-results-download-section'>
              <div className='screening-results-download-btn'>
                <div className='screening-results-section-text-padding'>
                  <div
                    className='screening-results-section-text'
                    onClick={() => setFileNameOpen(true)}
                  >
                    Download
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {type === 'Entities' &&
        !results.isLoading &&
        results.data.length >= 1 && (
        <>
          <EntityColumns
            showFilters={false}
            sortData={sortData}
            sortConfig={sortConfig}
          />
          <div className='screening-results-table-container'>
            {hitsData?.map((hit: any, index: number) => (
              <EntityRow
                showFilters={false}
                setShowFullRecord={setShowFullRecord}
                hit={hit}
                key={index}
              />
            ))}
          </div>
        </>
      )}
      {type === 'Products' &&
        !results.isLoading &&
        results.data.length >= 1 && (
        <>
          <ProductsColumns
            showFilters={false}
            sortData={sortData}
            sortConfig={sortConfig}
          />
          <div className='screening-results-table-container'>
            {hitsData?.map((hit: any, index: number) => (
              <ProductRow
                showFilters={false}
                setShowFullRecord={setShowFullRecord}
                hit={hit}
                key={index}
                isSelected={selectedProductHit === hit}
                onSelect={handleProductRowSelect}
              />
            ))}
          </div>
        </>
      )}
      {type === 'Cyber' && !results.isLoading && results.data.length >= 1 && (
        <>
          <CyberColumns
            showFilters={false}
            headers={cyberHeaders}
            sortData={sortData}
            sortConfig={sortConfig}
          />
          <div className='screening-results-table-container'>
            {hitsData?.map((hit: any, index: number) => (
              <CyberRow
                showFilters={false}
                setShowFullRecord={setShowFullRecord}
                hit={hit}
                key={index}
                tab={
                  cyberHeaders.includes('CVE ID') ?
                    'CVE' :
                    cyberHeaders?.includes('Intervention Type') ?
                      'Trade' :
                      'Markets'
                }
              />
            ))}
          </div>
        </>
      )}

      {!results.isLoading &&
        results?.data[0]?.result?.body?.hits?.total?.value === 0 && (
        <div className='screening-results-table-container'>
          <div
            className='no-screening-results-text'
            style={{
              width: '100%',
              padding: '24px 16px',
              boxSizing: 'border-box',
              textAlign: 'center',
            }}
          >
              No results found. Please try another search
          </div>
        </div>
      )}
      {totalResults > 0 && (
        <div className='screening-results-footer'>
          <div className='screening-results-pagination-text'>
            Page {page + 1} of {pageCount}
          </div>
          <div className='screening-results-footer-action-group'>
            <div className='hb-pagination'>
              <Pagination
                onChange={handlePageChange}
                size='large'
                count={pageCount}
                shape='rounded'
                variant='text'
                page={page + 1}
              />
            </div>
          </div>
        </div>
      )}
      {selectedProductHit && isProductDrawerOpen && (
        <div
          className='drawer-backdrop'
          onClick={() => setIsProductDrawerOpen(false)}
        >
          <aside
            className='drawer-panel entity-record-drawer product-record-drawer'
            onClick={(e) => e.stopPropagation()}
          >
            <div className='drawer-header entity-record-drawer-header'>
              <div className='entity-record-drawer-nav'>
                <button
                  type='button'
                  onClick={handleProductDrawerPrev}
                  disabled={!canNavigateProductPrev}
                  title='Open previous product'
                >
                  <ChevronLeftIcon sx={{ fontSize: 22 }} />
                </button>
                <button
                  type='button'
                  onClick={handleProductDrawerNext}
                  disabled={!canNavigateProductNext}
                  title='Open next product'
                >
                  <ChevronRightIcon sx={{ fontSize: 22 }} />
                </button>
              </div>
              <h2 className='drawer-title entity-record-drawer-title'>
                <span className='entity-record-drawer-title-main'>
                  {selectedProductSource['Product Name'] || 'Product Details'}
                </span>
              </h2>
              <button
                type='button'
                className='drawer-close-btn entity-record-drawer-close'
                onClick={() => setIsProductDrawerOpen(false)}
              >
                <CloseIcon sx={{ fontSize: 28 }} />
              </button>
            </div>
            <div className='drawer-body entity-record-drawer-body'>
              {selectedProductRiskSignals.length > 0 && (
                <div>
                  <div className='drawer-section-title'>Risk Signals</div>
                  <div className='drawer-kv-box entity-record-drawer-kv-box'>
                    {selectedProductRiskSignals.map(({ label, value }) => (
                      <div
                        className='drawer-kv-row entity-record-drawer-row'
                        key={label}
                      >
                        <span className='drawer-kv-label'>{label}</span>
                        <span className='drawer-kv-value'>
                          {renderFieldWithLinks(formatFieldValue(value))}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <div className='drawer-section-title'>Product Details</div>
                <div className='drawer-kv-box entity-record-drawer-kv-box'>
                  {Object.entries(selectedProductDetails)
                    .filter(([, value]) => hasDisplayValue(value))
                    .map(([label, value]) => (
                      <div
                        className='drawer-kv-row entity-record-drawer-row'
                        key={label}
                      >
                        <span className='drawer-kv-label'>{label}</span>
                        <span className='drawer-kv-value'>
                          {renderFieldWithLinks(formatFieldValue(value))}
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </aside>
        </div>
      )}
      <Modal
        isOpen={fileNameOpen}
        onRequestClose={handleFileNameClose}
        ariaHideApp={false}
      >
        <div className='bite-score-file-modal'>
          <div className='display-flex justify-between width-100'>
            <Typography variant='h5'>File Name:</Typography>
          </div>
          <Typography
            sx={{
              color: 'rgba(189, 190, 193, 0.87)',
              fontFamily: 'Inter',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineheight: '150%',
            }}
            variant='h6'
          >
            This will download the first 200 results
          </Typography>
          <div className='display-flex justify-end width-100'>
            <StyledInput
              value={fileName}
              onChange={handleFileNameChange}
              variant='outlined'
              className='margin-right-20px'
            />
            <NextButton
              variant='contained'
              color='primary'
              onClick={() => {
                handleCSV();
                handleFileNameClose();
              }}
            >
              Download
            </NextButton>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SingleScreeningResults;
